import type { TypedDocumentNode } from '@apollo/client';
import { gql, useSuspenseQuery } from '@apollo/client';
import { Suspense, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Position } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  PvEImplementation,
  TITLE,
} from '@sorare/core/src/components/Minigames/PvEImplementation';
import { NBA } from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isType } from '@sorare/core/src/lib/gql';
import { randomIntFromInterval } from '@sorare/core/src/lib/math';

import popupBackground from 'assets/minigames/popupBackground.webp';
import field from 'pages/minigames/assets/stadium.webp';
import {
  ALL_STAR,
  EASTERN_CONFERENCE,
  WESTERN_CONFERENCE,
} from 'pages/minigames/pve_/PLAYERS';

import type {
  PVENBAQuery,
  PVENBAQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

type NBACard = PVENBAQuery['anyCards'][number] & {
  __typename: 'NBACard';
  maskUrl?: string;
};

const POSITION = ['Guard', 'Guard', 'Forward', 'Forward', 'Forward'] as const;

const CONFERENCES_DATA_MAP = {
  western: {
    data: WESTERN_CONFERENCE,
    title: TITLE.NBA_WESTERN,
  },
  eastern: {
    data: EASTERN_CONFERENCE,
    title: TITLE.NBA_EASTERN,
  },
  'all-star': {
    data: ALL_STAR,
    title: TITLE.NBA_ALL_STAR,
  },
};

const buildData = (
  cards: ReadonlyArray<NBACard>,
  cardSlugs: Record<'GUARDS' | 'FWD', string[]>
) => {
  const data: [NBACard, NBACard][] = [];

  const Guards = cards.filter(card => cardSlugs.GUARDS.includes(card.slug));
  const Forwards = cards.filter(card => cardSlugs.FWD.includes(card.slug));

  // Mutable players to remove already selected players and avoid duplicate
  const playersData = {
    Guard: Guards.slice(),
    Forward: Forwards.slice(),
  };

  // All position players to serve as fallbacks if unable to avoid duplicates
  const allPlayers = {
    Guard: Guards,
    Forward: Forwards,
  };

  function takePlayer<T extends { slug: string }>(
    positionPlayers: T[],
    fallback: ReadonlyArray<T>,
    ...otherArrs: T[][]
  ): T {
    const search = positionPlayers.length ? positionPlayers : fallback.slice();

    const firstPlayerIndex = randomIntFromInterval(0, search.length - 1);
    const [player] = search.splice(firstPlayerIndex, 1);

    for (const arr of otherArrs) {
      const index = arr.findIndex(p => p.slug === player.slug);

      if (index >= 0) {
        // Remove from second array
        arr.splice(index, 1);
      }
    }

    return player;
  }

  POSITION.forEach(pos => {
    const positionPlayers = playersData[pos];
    const fallbackPlayers = allPlayers[pos];

    const otherPlayers = Object.values(playersData).filter(
      e => e !== positionPlayers
    );

    const firstPlayer = takePlayer(
      positionPlayers,
      fallbackPlayers,
      ...otherPlayers
    );
    const secondPlayer = takePlayer(
      positionPlayers,
      // Avoid showing the same player twice
      fallbackPlayers.filter(e => e.slug !== firstPlayer.slug),
      ...otherPlayers
    );

    data.push([firstPlayer, secondPlayer]);
  });
  return data;
};

const POSITIONS_MAP = [
  {
    position: Position.basketball_guard,
  },
  {
    position: Position.basketball_guard,
  },
  {
    position: Position.basketball_forward,
  },
  {
    position: Position.basketball_forward,
  },
  {
    position: Position.basketball_forward,
  },
];

const PVE_NBA_QUERY = gql`
  query PVENBAQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      ...PvEImplementation_anyCardInterface
    }
  }
  ${PvEImplementation.fragments.anyCardInterface}
` as TypedDocumentNode<PVENBAQuery, PVENBAQueryVariables>;

const PvEPageComponent = ({
  conference,
}: {
  conference: keyof typeof CONFERENCES_DATA_MAP;
}) => {
  const cardSlugs = CONFERENCES_DATA_MAP[conference].data;
  const navigate = useNavigate();
  const { signUp } = useConnectionContext();
  const { currentUser } = useCurrentUserContext();
  const handleNavigate = () => (currentUser ? navigate(NBA) : signUp());

  const { data } = useSuspenseQuery(PVE_NBA_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      slugs: [...cardSlugs.GUARDS, ...cardSlugs.FWD],
    },
  });

  const playersData = useMemo(
    () =>
      buildData(
        data.anyCards
          .filter(card => isType(card, 'NBACard'))
          .filter(e => e.score && e.score > 0),
        cardSlugs
      ),
    // regenerage random card slugs on retry
    // league is also required to trigger data regen on unknown league redirection (there might be a better way to do this)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conference]
  );

  const conferenceTitle = CONFERENCES_DATA_MAP[conference].title;

  return (
    <PvEImplementation
      key={conferenceTitle}
      playersData={playersData}
      fieldUrl={field}
      title={conferenceTitle}
      popupBackground={popupBackground}
      onClick={handleNavigate}
      positionsMap={POSITIONS_MAP}
    />
  );
};

const PvEPage = () => {
  const { conference } = useTypedParams<PageParams>();

  if (!Object.keys(CONFERENCES_DATA_MAP).includes(conference as string)) {
    return <Navigate replace to="/nba/minigames/pve/western" />;
  }

  return (
    <Suspense fallback={<LoadingIndicator fullScreen />}>
      <PvEPageComponent
        conference={conference as keyof typeof CONFERENCES_DATA_MAP}
      />
    </Suspense>
  );
};

export default PvEPage;
